/**
 * /transactions — unified one-off + recurring + transfer activity. Thin
 * shell that wires `useTransactions` into five pure view chunks plus
 * two supporting dialogs.
 *
 * Split from the original 1057-line single-file implementation per the
 * pattern in components/CONTRIBUTING.md.
 *
 *   pages/Transactions.js                    ← this shell, ~90 lines
 *   pages/transactions/
 *     useTransactions.js                     ← state + side effects
 *     TransactionsHeader.jsx                 ← title + account filter + Export + Import/Add buttons
 *     TransactionsFilters.jsx                ← frequency pills + search/date strip
 *     TransactionsTable.jsx                  ← table + empty state (incl. SortableTh)
 *     TransactionDialog.jsx                  ← Add/Edit form (largest chunk)
 *     ImportDialog.jsx                       ← CSV import + template
 *     constants.js                           ← FREQUENCY_FILTERS / LABELS / emptyForm
 */
import React from 'react';
import { fmtMoney } from '../lib/format';
import ConfirmDialog from '../components/ConfirmDialog';
import TierGate from '../components/TierGate';
import { useTransactions } from './transactions/useTransactions';
import TransactionsHeader from './transactions/TransactionsHeader';
import TransactionsFilters from './transactions/TransactionsFilters';
import TransactionsTable from './transactions/TransactionsTable';
import TransactionDialog from './transactions/TransactionDialog';
import ImportDialog from './transactions/ImportDialog';

const Transactions = () => {
  const t = useTransactions();

  if (t.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052FF]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" data-testid="transactions-page">
      <TransactionsHeader
        accounts={t.accounts}
        selectedAccount={t.selectedAccount}
        setSelectedAccount={t.setSelectedAccount}
        items={t.items}
        accountNameMap={t.accountNameMap}
        accountKindMap={t.accountKindMap}
        setImportDialogOpen={t.setImportDialogOpen}
        openNewDialog={t.openNewDialog}
      />

      <TransactionsFilters
        mechanicFilter={t.mechanicFilter}
        setMechanicFilter={t.setMechanicFilter}
        searchQuery={t.searchQuery}
        setSearchQuery={t.setSearchQuery}
        startDate={t.startDate}
        setStartDate={t.setStartDate}
        endDate={t.endDate}
        setEndDate={t.setEndDate}
        clearFilters={t.clearFilters}
      />

      <TransactionsTable
        items={t.items}
        sortConfig={t.sortConfig}
        toggleSort={t.toggleSort}
        accountNameMap={t.accountNameMap}
        accountKindMap={t.accountKindMap}
        handleEdit={t.handleEdit}
        handleDelete={t.handleDelete}
        openNewDialog={t.openNewDialog}
        setImportDialogOpen={t.setImportDialogOpen}
      />

      <TransactionDialog
        open={t.dialogOpen}
        onOpenChange={t.setDialogOpen}
        editingItem={t.editingItem}
        formData={t.formData}
        setFormData={t.setFormData}
        accounts={t.accounts}
        formIsLiability={t.formIsLiability}
        handleSubmit={t.handleSubmit}
      />

      <ImportDialog
        open={t.importDialogOpen}
        onOpenChange={t.setImportDialogOpen}
        accounts={t.accounts}
        importAccountId={t.importAccountId}
        setImportAccountId={t.setImportAccountId}
        importFile={t.importFile}
        handleFileChange={t.handleFileChange}
        fileInputRef={t.fileInputRef}
        handleImport={t.handleImport}
        downloadTemplate={t.downloadTemplate}
      />

      <ConfirmDialog
        open={!!t.pendingDelete}
        onOpenChange={(open) => { if (!open) t.setPendingDelete(null); }}
        title={t.pendingDelete?.mechanic === 'recurring' ? 'Delete this recurring transaction?' : 'Delete this transaction?'}
        description={
          t.pendingDelete
            ? `"${t.pendingDelete.description}" — £${fmtMoney(t.pendingDelete.amount)}. This cannot be undone.`
            : 'This cannot be undone.'
        }
        confirmLabel="Delete"
        onConfirm={t.confirmDelete}
        testId="delete-transaction-dialog"
      />

      <TierGate.Dialog
        open={!!t.importGateDetail}
        onOpenChange={(open) => { if (!open) t.setImportGateDetail(null); }}
        detail={t.importGateDetail}
        title="You've used your free CSV import for this account"
        testIdPrefix="csv-import-gate"
      />
    </div>
  );
};

export default Transactions;
